import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bus, Eye, ClipboardCheck, Car, Sliders, Coffee, PartyPopper, Ban,
    History, UserCheck, CalendarCheck, Rocket, CheckCircle2, AlertTriangle, UserPlus,
    FileText, ScanEye, GitCompare
} from 'lucide-react';

const ImportNombramientoAdmin = () => {
    const { token } = useAuth();
    const toast = useToast();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [batchProgress, setBatchProgress] = useState({
        total: 0,
        current: 0,
        success: 0,
        errors: [],
        isProcessing: false
    });

    // For single file preview (restoring previous behavior)
    const [previewResult, setPreviewResult] = useState(null);
    const [confirming, setConfirming] = useState(false);
    // NEW: Checkbox for historical mode (only saves to jornadas_reales)
    const [soloHistorico, setSoloHistorico] = useState(false);
    // Parse mode: 'text', 'vision', 'both'
    const [parseMode, setParseMode] = useState('vision');
    // Group: 'bei', 'servicios'
    const [userGroup, setUserGroup] = useState('bei');

    const fileInputRef = useRef(null);

    // Categories configuration for display
    // Categories configuration for display
    const categories = [
        { key: 'trabajo', label: 'Trabajo (Linea)', color: '#0ea5e9', icon: <Bus className="w-6 h-6" /> },
        { key: 'vacaciones', label: 'Vacaciones', color: '#10b981', icon: <CalendarCheck className="w-6 h-6" /> },
        { key: 'supervision', label: 'Supervision', color: '#f59e0b', icon: <Eye className="w-6 h-6" /> },
        { key: 'revision', label: 'Revision', color: '#6366f1', icon: <ClipboardCheck className="w-6 h-6" /> },
        { key: 'revisionConduccion', label: 'Revision-Conduccion', color: '#8b5cf6', icon: <Car className="w-6 h-6" /> },
        { key: 'bloqueAjuste', label: 'Bloque de Ajuste', color: '#06b6d4', icon: <Sliders className="w-6 h-6" /> },
        { key: 'descanso', label: 'Descanso Semanal', color: '#f97316', icon: <Coffee className="w-6 h-6" /> },
        { key: 'fiestaCompensada', label: 'Fiesta Compensada', color: '#ec4899', icon: <PartyPopper className="w-6 h-6" /> },
        { key: 'anexoLibranzas', label: 'Anexo Libranzas', color: '#22c55e', icon: <Rocket className="w-6 h-6" /> },
        { key: 'compAnosAnt', label: 'Comp. Anos Ant.', color: '#eab308', icon: <History className="w-6 h-6" /> },
        { key: 'asuntosPropios', label: 'Asuntos Propios', color: '#14b8a6', icon: <UserCheck className="w-6 h-6" /> },
        { key: 'libreConvenio', label: 'Libre Convenio', color: '#84cc16', icon: <CalendarCheck className="w-6 h-6" /> },
        { key: 'noDisponible', label: 'No Disponible', color: '#6b7280', icon: <Ban className="w-6 h-6" /> },
        { key: 'especial', label: 'Servicios Especiales', color: '#f43f5e', icon: <Rocket className="w-6 h-6" /> },
        { key: 'baja', label: 'Bajas / Ausencias', color: '#94a3b8', icon: <Ban className="w-6 h-6" /> },
    ];

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files);
            setFiles(selectedFiles);
            setPreviewResult(null);
            setResults(null);
            setBatchProgress({
                total: selectedFiles.length,
                current: 0,
                success: 0,
                errors: [],
                isProcessing: false
            });
        }
    };

    const processSingleFile = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', parseMode);
        formData.append('group', userGroup);

        try {
            // 1. Analysis
            const parseData = await api.postFormData('/admin/import-nombramiento', formData);

            if (!parseData.details?.entries || !parseData.details?.date) {
                throw new Error('No se detectaron datos válidos en el archivo');
            }

            // 2. Auto-Confirm
            const confirmResponse = await api.post('/admin/confirm-import', {
                date: parseData.details.date,
                entries: parseData.details.entries,
                soloHistorico: soloHistorico // Pass the checkbox state
            });

            return {
                success: true,
                date: parseData.details.date,
                totalUpdated: confirmResponse.results.totalUpdated,
                fileName: file.name
            };
        } catch (error) {
            return {
                success: false,
                fileName: file.name,
                error: error.message
            };
        }
    };

    const handleBatchProcess = async () => {
        if (files.length === 0) {
            toast.error('Por favor selecciona archivos PDF');
            return;
        }

        setLoading(true);
        setBatchProgress(prev => ({ ...prev, isProcessing: true, current: 0, success: 0, errors: [] }));

        const batchResults = [];
        let successCount = 0;
        const errors = [];

        for (let i = 0; i < files.length; i++) {
            setBatchProgress(prev => ({ ...prev, current: i + 1 }));

            const result = await processSingleFile(files[i]);

            if (result.success) {
                successCount++;
            } else {
                errors.push({ name: result.fileName, error: result.error });
            }
            batchResults.push(result);
        }

        setBatchProgress(prev => ({
            ...prev,
            isProcessing: false,
            success: successCount,
            errors: errors
        }));

        setLoading(false);
        setResults({ isBatch: true, results: batchResults });

        if (errors.length === 0) {
            toast.success(`Procesamiento masivo completado: ${successCount} días importados.`);
        } else {
            toast.warning(`Completado con errores: ${successCount} éxitos, ${errors.length} fallos.`);
        }
    };

    // Restored: Single file preview
    const handleSinglePreview = async () => {
        if (files.length !== 1) return;
        setLoading(true);
        setPreviewResult(null);
        const formData = new FormData();
        formData.append('file', files[0]);
        formData.append('mode', parseMode);
        formData.append('group', userGroup);
        try {
            const data = await api.postFormData('/admin/import-nombramiento', formData);
            setPreviewResult(data);
            toast.success(`Vista previa cargada para ${data.details.date}`);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSingle = async () => {
        if (!previewResult?.details?.entries || !previewResult?.details?.date) return;
        setConfirming(true);
        try {
            const response = await api.post('/admin/confirm-import', {
                date: previewResult.details.date,
                entries: previewResult.details.entries,
                soloHistorico: soloHistorico // Pass the checkbox state
            });
            toast.success(response.message || 'Datos guardados correctamente');
            setPreviewResult({
                ...previewResult,
                preview: false,
                finalResults: response.results
            });
            setFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            toast.error(error.message);
        } finally {
            setConfirming(false);
        }
    };

    // Helper for summary count in preview
    const getTotalPreviewUpdated = () => {
        return previewResult?.details?.totalEntries || 0;
    };

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent">
                Importación de Nombramientos
            </h1>

            <div className="card p-6 border border-white/10 bg-white/5 rounded-xl backdrop-blur-sm">
                <div className="space-y-4">
                    <p className="text-gray-300">
                        {files.length > 1
                            ? "Selección múltiple detectada. Se procesarán todos los archivos automáticamente."
                            : "Sube el archivo PDF del Nombramiento. Si subes varios, podrás procesarlos en lote."}
                    </p>

                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                            <input
                                type="file"
                                accept=".pdf"
                                multiple
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                className="block w-full text-sm text-gray-400
                                    file:mr-4 file:py-2.5 file:px-4
                                    file:rounded-xl file:border-0
                                    file:text-sm file:font-semibold
                                    file:bg-sky-500/10 file:text-sky-400
                                    hover:file:bg-sky-500/20
                                    cursor-pointer"
                            />

                            {files.length > 1 ? (
                                <button
                                    onClick={handleBatchProcess}
                                    disabled={loading}
                                    className={`px-8 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap
                                        ${loading
                                            ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                            : 'bg-sky-500 hover:bg-sky-600 text-white shadow-lg shadow-sky-500/20 active:scale-95'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <Rocket className="w-5 h-5" />
                                            Importación Masiva ({files.length})
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={handleSinglePreview}
                                    disabled={files.length === 0 || loading}
                                    className={`px-8 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap
                                        ${files.length === 0 || loading
                                            ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                            : 'bg-sky-500 hover:bg-sky-600 text-white shadow-lg shadow-sky-500/20 active:scale-95'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Analizando...
                                        </>
                                    ) : (
                                        <>
                                            <Eye className="w-5 h-5" />
                                            Analizar y Previsualizar
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Selector de Grupo */}
                        <div className="flex flex-col gap-3 px-4 py-3 bg-white/5 rounded-xl border border-white/10">
                            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Grupo de conductores</span>
                            <div className="flex gap-2">
                                {[
                                    { key: 'bei', label: 'BEI', icon: <Bus className="w-4 h-4" />, desc: 'Líneas BEI-A / BEI-B' },
                                    { key: 'servicios', label: 'Servicios', icon: <UserCheck className="w-4 h-4" />, desc: 'Líneas Convencionales Lxx' }
                                ].map(g => (
                                    <button
                                        key={g.key}
                                        onClick={() => setUserGroup(g.key)}
                                        className={`flex-1 flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg border transition-all text-sm ${userGroup === g.key
                                                ? 'bg-sky-500/20 border-sky-500/50 text-sky-300 shadow-lg shadow-sky-500/10'
                                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-1.5 font-medium">
                                            {g.icon}
                                            {g.label}
                                        </div>
                                        <span className="text-[10px] opacity-70">{g.desc}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Selector de Modo de Análisis */}
                        <div className="flex flex-col gap-3 px-4 py-3 bg-white/5 rounded-xl border border-white/10">
                            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">Modo de análisis</span>
                            <div className="flex gap-2">
                                {[
                                    { key: 'text', label: 'Texto', icon: <FileText className="w-4 h-4" />, desc: 'Extracción de texto + IA' },
                                    { key: 'vision', label: 'Visión', icon: <ScanEye className="w-4 h-4" />, desc: 'IA ve el PDF directamente' },
                                    { key: 'both', label: 'Ambos', icon: <GitCompare className="w-4 h-4" />, desc: 'Compara ambos métodos' }
                                ].map(m => (
                                    <button
                                        key={m.key}
                                        onClick={() => setParseMode(m.key)}
                                        className={`flex-1 flex flex-col items-center gap-1 px-3 py-2.5 rounded-lg border transition-all text-sm ${parseMode === m.key
                                                ? 'bg-sky-500/20 border-sky-500/50 text-sky-300 shadow-lg shadow-sky-500/10'
                                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-1.5 font-medium">
                                            {m.icon}
                                            {m.label}
                                        </div>
                                        <span className="text-[10px] opacity-70">{m.desc}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Opciones de Importación */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-xl border border-white/10">
                            <input
                                type="checkbox"
                                id="soloHistorico"
                                checked={soloHistorico}
                                onChange={(e) => setSoloHistorico(e.target.checked)}
                                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-sky-500 focus:ring-sky-500 focus:ring-offset-gray-800"
                            />
                            <label htmlFor="soloHistorico" className="text-gray-300 text-sm select-none cursor-pointer flex flex-col">
                                <span className="font-medium">Solo guardar histórico</span>
                                <span className="text-xs text-gray-500">Guarda en BD para consultas pero NO actualiza el calendario visible</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress UI for Batch */}
            <AnimatePresence>
                {batchProgress.isProcessing && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="card p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4"
                    >
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2">
                                <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
                                Procesando Lote ({batchProgress.current} de {batchProgress.total})
                            </h3>
                            <span className="text-sky-400 font-mono">
                                {Math.round((batchProgress.current / batchProgress.total) * 100)}%
                            </span>
                        </div>

                        <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-sky-500 shadow-[0_0_10px_rgba(14,165,233,0.5)]"
                                initial={{ width: 0 }}
                                animate={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                            />
                        </div>
                        <p className="text-sm text-gray-400 italic">
                            Procesando: <span className="text-gray-200">{files[batchProgress.current - 1]?.name}</span>
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Single Preview Results */}
            <AnimatePresence>
                {previewResult && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="card p-4 bg-emerald-500/10 border-emerald-500/20 border rounded-xl">
                                <h3 className="text-emerald-400 font-medium mb-1">Fecha Detectada</h3>
                                <p className="text-2xl font-bold text-white">{previewResult.details.date}</p>
                            </div>
                            <div className="card p-4 bg-blue-500/10 border-blue-500/20 border rounded-xl">
                                <h3 className="text-blue-400 font-medium mb-1">Total Registros</h3>
                                <p className="text-2xl font-bold text-white">{previewResult.details.totalEntries}</p>
                            </div>
                            <div className="card p-4 bg-purple-500/10 border-purple-500/20 border rounded-xl">
                                <h3 className="text-purple-400 font-medium mb-1">Conductores Procesados</h3>
                                <p className="text-2xl font-bold text-white">{getTotalPreviewUpdated()}</p>
                            </div>
                        </div>

                        {/* Modo de análisis usado */}
                        {previewResult.details.parseMode && (
                            <div className="text-xs text-gray-500 text-right">
                                Analizado con: <span className="text-gray-300 font-medium">
                                    {previewResult.details.parseMode === 'text' ? '📄 Texto' : previewResult.details.parseMode === 'vision' ? '👁️ Visión' : '🔍 Ambos'}
                                </span>
                            </div>
                        )}

                        {/* Breakdown by Category */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {categories.map(cat => {
                                const count = previewResult.details.breakdown?.[cat.key] || 0;
                                if (count === 0) return null;
                                return (
                                    <div
                                        key={cat.key}
                                        className="card p-3 border rounded-xl flex items-center gap-3"
                                        style={{ backgroundColor: `${cat.color}15`, borderColor: `${cat.color}40` }}
                                    >
                                        <div style={{ color: cat.color }}>{cat.icon}</div>
                                        <div>
                                            <p className="text-xs" style={{ color: cat.color }}>{cat.label}</p>
                                            <p className="text-xl font-bold text-white">{count}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Panel de Comparación (solo modo 'both') */}
                        {previewResult.details.comparison && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-bold flex items-center gap-2 text-amber-400">
                                    <GitCompare className="w-5 h-5" />
                                    Comparación: Texto vs Visión
                                </h3>

                                {previewResult.details.comparison.match ? (
                                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                                        <div>
                                            <p className="text-emerald-300 font-medium">¡Ambos métodos coinciden al 100%!</p>
                                            <p className="text-xs text-emerald-400/60">No se encontraron incoherencias entre texto y visión</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                                            <p className="text-amber-300 text-sm font-medium flex items-center gap-2">
                                                <AlertTriangle className="w-4 h-4" />
                                                {previewResult.details.comparison.discrepancies.length} incoherencia(s) detectada(s)
                                            </p>
                                        </div>
                                        {previewResult.details.comparison.discrepancies.map((d, idx) => (
                                            <div key={idx} className="p-3 bg-red-500/5 border border-red-500/15 rounded-lg">
                                                <p className="text-xs text-gray-400 mb-1 font-mono">{d.section || d.type}</p>
                                                <p className="text-sm text-gray-300">{d.message}</p>
                                                {d.onlyInText?.length > 0 && (
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        <span className="text-[10px] text-blue-400">Solo en Texto:</span>
                                                        {d.onlyInText.map((n, i) => (
                                                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">{n}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {d.onlyInVision?.length > 0 && (
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        <span className="text-[10px] text-purple-400">Solo en Visión:</span>
                                                        {d.onlyInVision.map((n, i) => (
                                                            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">{n}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Mini tabla de conteo lado a lado */}
                                        <div className="mt-3 grid grid-cols-2 gap-3">
                                            <div className="p-3 bg-blue-500/5 border border-blue-500/15 rounded-lg">
                                                <p className="text-xs text-blue-400 font-medium mb-2">📄 Resultado Texto</p>
                                                {categories.map(cat => {
                                                    const count = previewResult.details.comparison.textResult?.summary?.[cat.key] || 0;
                                                    if (count === 0) return null;
                                                    return (
                                                        <div key={cat.key} className="flex justify-between text-xs py-0.5">
                                                            <span className="text-gray-400">{cat.label}</span>
                                                            <span className="text-white font-mono">{count}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="p-3 bg-purple-500/5 border border-purple-500/15 rounded-lg">
                                                <p className="text-xs text-purple-400 font-medium mb-2">👁️ Resultado Visión</p>
                                                {categories.map(cat => {
                                                    const count = previewResult.details.comparison.visionResult?.summary?.[cat.key] || 0;
                                                    if (count === 0) return null;
                                                    return (
                                                        <div key={cat.key} className="flex justify-between text-xs py-0.5">
                                                            <span className="text-gray-400">{cat.label}</span>
                                                            <span className="text-white font-mono">{count}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Updated Aliases (Table style for 'trabajo') */}
                        {previewResult.details.updatedAliases?.trabajo && (
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold flex items-center gap-2 text-sky-400">
                                    <Bus className="w-6 h-6" />
                                    Asignaciones de Línea
                                </h3>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* Grouping display: adapta a BEI o Servicios */}
                                    {Object.entries(previewResult.details.updatedAliases.trabajo.reduce((acc, item) => {
                                        const rawLinea = String(item.linea || '').toUpperCase().trim();
                                        const rawServidor = String(item.servidor || '').toUpperCase().trim();
                                        let key;
                                        if (userGroup === 'servicios') {
                                            // Servicios: agrupar por la línea real (L01, L03, ESPECIAL...)
                                            const servidorPart = rawServidor ? ` / ${rawServidor}` : '';
                                            key = `${rawLinea || 'OTRAS'}${servidorPart}`;
                                        } else {
                                            // BEI: lógica original para mapear a BEI-A/BEI-B
                                            const servidor = rawServidor.replace(/[- ]/g, '') || (rawLinea.match(/^([AB])[- ]?(\d)$/) ? rawLinea.replace(/[- ]/g, '') : '');
                                            const linea = /^BEI-[AB]$/.test(rawLinea)
                                                ? rawLinea
                                                : (servidor ? `BEI-${servidor[0]}` : (rawLinea || 'OTRAS'));
                                            key = servidor ? `${linea} / ${servidor}` : linea;
                                        }
                                        if (!acc[key]) acc[key] = [];
                                        acc[key].push(item);
                                        return acc;
                                    }, {})).map(([line, items]) => (
                                        <div key={line} className="card p-4 bg-white/5 border border-white/10 rounded-xl">
                                            <h4 className="font-bold text-sky-400 mb-2 border-b border-white/10 pb-1">{line}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {items.map((it, idx) => (
                                                    <span key={idx} className="text-xs bg-white/5 px-2 py-1 rounded border border-white/10 text-gray-300">
                                                        {it.alias}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Eventuales */}
                        {previewResult.details.notFoundAliases?.length > 0 && (
                            <div className="card p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                                <h3 className="text-amber-400 font-bold mb-2 flex items-center gap-2">
                                    <UserPlus className="w-5 h-5" />
                                    Eventuales detected ({previewResult.details.notFoundAliases.length})
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {previewResult.details.notFoundAliases.map((alias, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded border border-amber-500/30">{alias}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Confirmation Button */}
                        {previewResult.preview && (
                            <div className="flex justify-center pt-4">
                                <button
                                    onClick={handleConfirmSingle}
                                    disabled={confirming}
                                    className={`px-12 py-3 rounded-2xl font-bold text-lg transition-all flex items-center gap-3
                                        ${confirming
                                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                            : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-xl shadow-emerald-500/20 active:scale-95'
                                        }`}
                                >
                                    {confirming ? "Guardando..." : "CONFIRMAR Y GUARDAR"}
                                </button>
                            </div>
                        )}

                        {!previewResult.preview && previewResult.finalResults && (
                            <div className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl text-center flex flex-col items-center">
                                <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-2" />
                                <h3 className="text-lg font-bold text-white">¡Guardado con éxito!</h3>
                                <p className="text-emerald-400/80">Se actualizó a {previewResult.finalResults.totalUpdated} conductores.</p>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Batch Results UI */}
            <AnimatePresence>
                {results && results.isBatch && !loading && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="space-y-4"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="card p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
                                <p className="text-xs text-emerald-400 uppercase font-bold tracking-wider">Éxitos</p>
                                <p className="text-3xl font-bold text-white">{batchProgress.success}</p>
                            </div>
                            <div className="card p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                                <p className="text-xs text-red-400 uppercase font-bold tracking-wider">Errores</p>
                                <p className="text-3xl font-bold text-white">{batchProgress.errors.length}</p>
                            </div>
                            <div className="card p-4 bg-sky-500/10 border border-sky-500/20 rounded-xl text-center">
                                <p className="text-xs text-sky-400 uppercase font-bold tracking-wider">Total</p>
                                <p className="text-3xl font-bold text-white">{batchProgress.total}</p>
                            </div>
                        </div>

                        {batchProgress.errors.length > 0 && (
                            <div className="card p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                                <h4 className="text-red-400 font-bold mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5" />
                                    Detalle de errores
                                </h4>
                                <ul className="space-y-1">
                                    {batchProgress.errors.map((err, i) => (
                                        <li key={i} className="text-xs text-gray-400 flex justify-between">
                                            <span>{err.name}</span>
                                            <span className="text-red-400/70">{err.error}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex justify-center pt-4">
                            <button
                                onClick={() => {
                                    setResults(null);
                                    setFiles([]);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                                className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-gray-300 transition-colors"
                            >
                                Limpiar y volver
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div >
    );
};

export default ImportNombramientoAdmin;


